import React, { useState, useEffect, useCallback } from 'react';

import { ToastProvider } from './context/ToastContext';
import { useGlobalVariablesContext } from './context/GlobalVariablesContext';
import { ToolsContext } from './context/ToolsContext';
import useBootstrapTheme from './hooks/useBootstrapTheme';
import { useTranslation } from 'react-i18next';
import { usePermission } from './hooks/usePermission';
import { canCreateIncidence } from './services/incidenceService';
import { getVisorID } from './utils/misc';

import Map from "./components/Map";
import ToolsContainer from './components/ToolsContainer/ToolsContainer';
import ToastNotification from './components/ToastNotification/ToastNotification';
import {
    MoreTools, ZoomComponent, PanComponent, CenterOnCoordinates,
    StylesAppLoader, Layers, Layer, NominatimSearch, Creditos, MousePositionControl, HomeButton,
    LayerSelector, AttributeQuery, MeasureLineal, MeasurePolygon,
    CenterMapOnCoordinates, Spinner, LoadAdditionalLayers,
    CadastroSelector, PopUpComponent, QueriesTool, PrintMap, AddIncidence,
    CenterOnFeature
} from "./components";
import ToolboxMenuItems from './components/ToolboxMenuItems/ToolboxMenuItems';

import RightSidebar from './components/RightSidebar/RightSidebar';
import { normalizeTools, isToolEnabled } from './utils/toolsHelper';
import Loading from './components/Bootstrap/Spinner/Loading';
import BusyXhrIndicator from './components/helpers/busyHrxIndicator';
import CatastroLayerController from './components/Cadastro/CatastroLayerController/CatastroLayerController';

const App = () => {

    const { t } = useTranslation();
    const { config, isLoading, layerCadastro, setLayerCadastro } = useGlobalVariablesContext();

    // Aplicar tema dinámico de Bootstrap
    useBootstrapTheme();

    const [coordinatesToCenter, setCoordinatesToCenter] = useState(null);
    const [toolStates, setToolStates] = useState({});

    // Estados para controlar la carga progresiva
    const [uiLoaded, setUiLoaded] = useState(false);
    const [layersLoaded, setLayersLoaded] = useState(false);

    // Verificar si el usuario puede crear incidencias
    const mapViewerId = getVisorID();
    const { allowed: canAddIncidence, loading: checkingIncidencePermission } = usePermission(
        mapViewerId ? 'incidence.create' : null,
        (signal) => canCreateIncidence(mapViewerId, { signal })
    );

    /** Actualiza el estado de una herramienta específica específica dentro del objeto global toolStates.
     * 
     * Si la herramienta se activa (newState.active === true), desactiva automáticamente las demás herramientas
     * que estén activas y no sean del tipo 'clickable'.
     * 
     * @param {string} id - El identificador único de la herramienta a actualizar.
     * @param {Object} newState - El nuevo estado de la herramienta, con propiedades como:
     *   - active: boolean que indica si la herramienta está activa.
     *   - sidebarOpen: boolean para abrir/cerrar su panel lateral.
     *   - type: tipo de herramienta ('clickable' u otros).
     */
    const setToolState = useCallback((id, newState) => {
        // Si el usuario activa cualquier herramienta distinta a Catastro, desactivar la capa de catastro
        // (Catastro vive en estado global `layerCadastro`, independiente del estado "active" de la herramienta).
        if (newState?.active === true && id !== 'cadastro_button' && layerCadastro) {
            setLayerCadastro(false);
        }

        setToolStates(prev => {
            // Creamos una copia del estado anterior y actualizamos el estado de la herramienta con id `id`
            const updatedStates = {
                ...prev,
                [id]: {
                    ...prev[id],           // Mantenemos cualquier estado anterior de esa herramienta
                    ...newState,           // Aplicamos el nuevo estado recibido
                    type: newState.type !== undefined ? newState.type : prev[id]?.type, // Si no se pasa el 'type', se conserva el anterior
                },
            };

            // Si la herramienta que se actualiza se está activando...
            if (newState.active) {
                // Iteramos por todas las herramientas para desactivar las que no sean 'clickable' y estén activas
                Object.keys(updatedStates).forEach(toolId => {
                    if (
                        toolId !== id &&                       // No es la herramienta actual
                        updatedStates[toolId].active           // Está activa
                        // && updatedStates[toolId].type !== 'clickable' // No es de tipo 'clickable'
                    ) {
                        updatedStates[toolId] = {
                            ...updatedStates[toolId],
                            active: false,       // La desactivamos
                            sidebarOpen: false, // Cerramos su panel lateral si lo tuviera abierto
                        };
                    }
                });
            }

            return updatedStates; // Devolvemos el nuevo estado global
        });
    }, [layerCadastro, setLayerCadastro, setToolStates]);

    useEffect(() => {
        // Fase 1: Cargar UI inmediatamente
        setUiLoaded(true);

        // Fase 2: Cargar layers después de un pequeño delay
        const layersTimer = setTimeout(() => {
            setLayersLoaded(true);
        }, 100);

        return () => {
            clearTimeout(layersTimer);
        };
    }, []);

    if (!config) {
        return <Loading text={t('Loading configuration')} />;
    }

    const layers = config.LAYERS || [];
    const groups = config.GROUPS || [];
    const tools = normalizeTools(config.TOOLS) || [];

    // Renderizar la UI básica (botonera)
    const renderTools = () => (
        <ToolsContainer>
            {uiLoaded && (
                <>
                    {isToolEnabled(tools, 'homeButton') && (<HomeButton />)}
                    {isToolEnabled(tools, 'zoomComponent') && (<ZoomComponent />)}
                    {isToolEnabled(tools, 'layerSelector') && (<LayerSelector />)}
                    {isToolEnabled(tools, 'attributeQuery') && (<AttributeQuery />)}
                    {isToolEnabled(tools, 'centerOnFeature') && (<CenterOnFeature />)}
                    {isToolEnabled(tools, 'queriesTool') && (<QueriesTool />)}

                    {isToolEnabled(tools, 'cadastroSelector') && (<CadastroSelector />)}
                    {isToolEnabled(tools, 'loadAdditionalLayer') && (<LoadAdditionalLayers />)}
                    
                    {isToolEnabled(tools, 'panComponent') && (<PanComponent />)}

                    {isToolEnabled(tools, 'measureLineal') && (<MeasureLineal />)}
                    {isToolEnabled(tools, 'measurePolygon') && (<MeasurePolygon />)}
                    {isToolEnabled(tools, 'centerOnCoordinates') && (<CenterOnCoordinates />)}
                    {isToolEnabled(tools, 'printMap') && (<PrintMap />)}
                    {/* {isToolEnabled(tools, 'moreTools') && (<MoreTools />)} */}

                    {isToolEnabled(tools, 'toolboxMenuItems') && (<ToolboxMenuItems />)}
                    {canAddIncidence && (<AddIncidence />)}
                </>
            )}
        </ToolsContainer>
    );

    // Renderizar las capas
    const renderLayers = () => (
        layersLoaded && (
            <Layers>
                {layers
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((layer) => (
                        <Layer
                            key={layer.name + layer.title}
                            {...layer}
                        />
                    ))
                }
                {groups && groups.map((group) => (
                    <div key={group.name}>
                        {group.layers && group.layers.map((layer) => (
                            <Layer
                                key={layer.name}
                                visible={group.visible}
                                {...layer}
                                group={group.name}
                            />
                        ))}
                    </div>
                ))}
            </Layers>
        )
    );


    return (
        <div>
            {isLoading && <Spinner />}
            <ToolsContext.Provider value={{ toolStates, setToolState }}>
                <ToastProvider>
                    <StylesAppLoader />
                    <ToastNotification />
                    <div className='map-panel'>
                        <Map>
                            {/* Fase 1: UI Básica */}
                            {isToolEnabled(tools, 'nominatimSearch') && (<NominatimSearch />)}
                            {isToolEnabled(tools, 'credits') && (<Creditos />)}
                            {isToolEnabled(tools, 'mousePositionControl') && (<MousePositionControl />)}
                            {renderTools()}
                            {/* Controlador de Catastro: mantiene la capa activa aunque el sidebar se cierre o cambies de pestaña */}
                            {isToolEnabled(tools, 'cadastroSelector') && (<CatastroLayerController />)}

                            {/* Barra de herramientas derecha */}
                            {/* {tools.toolbarRight?.enable && (
                                    <ToolsContainerRight>
                                        {tools.homeButton?.enable && (<HomeButton />)}
                                        {tools.homeButton?.enable && (<HomeButton />)}
                                        {tools.homeButton?.enable && (<HomeButton />)}
                                    </ToolsContainerRight>
                                )} */}

                            {/* Sidebar derecho con leyendas */}
                            <RightSidebar />

                            {/* Fase 2: Layers */}
                            {renderLayers()}


                            <CenterMapOnCoordinates coordinatesToCenter={coordinatesToCenter} />
                            <PopUpComponent />
                            <BusyXhrIndicator />
                        </Map>
                    </div>
                </ToastProvider>
            </ToolsContext.Provider>
        </div>
    );
};

export default App;